import { useState } from 'react'
import { Badge, Box, Group, Text, UnstyledButton } from '@mantine/core'
import { useFormat } from '../../_units'
import { useI18n } from '../../_i18n'
import { TILE } from '../_tiles'
import { WeekRow } from '../_week'
import {
  conditionLabel,
  isNightHour,
  precipConditionCode,
  thunderSignificant,
  warningTypeLabel,
  warningsForDay,
  WARNING_SEVERITY_COLOR,
} from './weatherData'
import Caption, { RowUnit } from './Caption'
import { RowLabel, Stat } from './bandRow'
import { conditionGlyph } from './ConditionGlyph'
import DirectionLabel from './DirectionLabel'
import WindSpeedChart from './WindSpeedChart'
import { CAPTION_LS, DATA_FZ, GLYPH_FZ, TILE_TEXT } from './railStyle'

const MUTED = TILE_TEXT.muted
const BORDER = '1px solid rgba(255,255,255,.14)'

// Content for one day's Weather band inside its WeekCard, between Tides and
// Sun. Built from the same WeekRow the bands either side of it use, but with
// two related readings per row rather than one — the middle and right cells
// both carry data, the way the Tides and Moon rows already fill all three:
//
//   Temperature   85° / 84° air      85° water
//   Chances              27% rain    20% thunder
//   Max seas        1.7' ↖ SE @ 5s
//   Visibility                8 nm
//   [ wind chart, inset ]
//
// then a click-to-expand hourly breakdown. Pairing is what keeps this at
// three rows: one row per reading is the same grammar but makes weather the
// tallest section in a card whose subject is tides, and a centered layout of
// its own reads as a foreign object in a card of label rows. WeekRow's middle
// cell holds a time for Tides/Sun/Moon; weather has none to put there, every
// daily reading being an aggregate over the whole day, so it takes the first
// reading instead.
//
// The first two rows take a label that covers the whole row, so BOTH their
// readings carry the word naming them — neither "85° / 84°" nor "27%" says
// which is which on its own. That also lets a row survive either reading
// being absent: with no air temp the water value simply moves left, still
// named, under a label that was never air-specific, and likewise for
// rain/thunder.
//
// The precipitation word names what is actually falling — rain, snow, sleet,
// freezing rain — via precipConditionCode, because the number itself
// (precip_probability_max_pct) is precipitation of any kind and a fixed
// "rain" would simply be wrong all winter.
//
// Every cell is conditional on the feed actually having the number:
// precip/thunder are often absent past the ~36h horizon, seas is genuinely
// unavailable at sheltered stations (see the `seas_unavailable` note code),
// and water temp is missing wherever the SST grid doesn't reach.
//
// The day's condition glyph is NOT in here — it's the band header's emoji
// (dayConditionGlyph, which also carries the thunder override), so the day
// gets exactly one icon rather than one in the header and another repeating
// it a few pixels below.
//
// A daily row uses ONE size (DATA_FZ.value) and ONE color (white). Weight is
// the only thing separating a reading from the word naming it — bold number,
// regular word — which is the same contrast the Tides/Sun/Moon rows already
// draw between a bold "High" and a regular "3:41 AM". Anything in these three
// rows rendering at a second size, in gray, uppercase, or with tracking is a
// bug; those were four separate axes of difference that together made this
// band read as a different kind of object from the ones around it.
//
// The two places that legitimately look different are both outside the rows:
// the hourly table's column headings, which label a column rather than being
// readings themselves (see Caption), and the wind chart's monospace axis
// numbers, which are instrument readings against a scale rather than prose
// (see MONO_FONT in railStyle).
export default function DayWeatherBand({ day, hours, timezone, date, warnings }) {
  const { t } = useI18n()
  const fmt = useFormat()
  const [open, setOpen] = useState(false)

  // The day's warnings, badged above the readings. These were dropped when this
  // band replaced DayWeatherSection: the restructure was scoped to readings and
  // took the badges with it, leaving the week view silent about a warning it
  // used to show. A reader scanning seven days for when it blows should not have
  // to infer it from the wind chart.
  //
  // Warnings only, not hazards. The band already carries thunder and precip
  // chances and the header glyph already applies the thunder override, so a
  // "thunderstorms" chip beside "THUNDER 31%" would be a third rendering of one
  // fact. A warning duplicates nothing here.
  const dayWarnings = warningsForDay(warnings, date, timezone)

  const lo = day.air_temp_f?.min
  const hi = day.air_temp_f?.max
  const wsust = day.wind_max_kt
  const gmax = day.gusts_max_kt

  let peakSeas = null
  let peakWind = null
  const speeds = []
  for (const h of hours) {
    if (h.seas && isFinite(h.seas.height_ft)) {
      if (!peakSeas || h.seas.height_ft > peakSeas.seas.height_ft) peakSeas = h
    }
    if (h.wind && isFinite(h.wind.speed_kt)) {
      if (!peakWind || h.wind.speed_kt > peakWind.wind.speed_kt) peakWind = h
      speeds.push(h.wind.speed_kt)
    }
  }
  const wmin = speeds.length ? Math.min(...speeds) : wsust

  // A day with neither bound is no temperature at all — never a bare "AIR"
  // caption over an empty "/" — but one bound alone still reads fine.
  const airShown = hi != null || lo != null
  const waterShown = day.water_temp_mean_f != null

  const dayPp = day.precip_probability_max_pct
  const dayPpShown = dayPp != null && Math.round(dayPp) > 15
  const dayThunderPct = day.thunder_worst?.probability_pct
  // Same bar HourRow uses: a flagged (official forecaster) day shows its
  // number down to any real risk; an unflagged one needs to clear >10% to be
  // worth a number. Independent of thunderSignificant, which only gates the
  // line-1 glyph override and uses a higher bar (>=30%) for that.
  const dayThunderShown =
    dayThunderPct != null && (day.thunder_worst?.flagged ? dayThunderPct > 0 : dayThunderPct > 10)

  return (
    <>
      {dayWarnings.length > 0 && (
        <Group gap={6} wrap="wrap" px={16} pb={10}>
          {dayWarnings.map((w) => (
            <Badge
              key={w.type}
              color={WARNING_SEVERITY_COLOR[w.severity] || 'gray'}
              size="sm"
              autoContrast
            >
              {warningTypeLabel(t, w.type)}
            </Badge>
          ))}
        </Group>
      )}
      {(airShown || waterShown) && (
        <WeekRow
          left={<RowLabel>{t('temperature')}</RowLabel>}
          time={
            airShown ? (
              <Stat value={`${fmt.temp(hi)} / ${fmt.temp(lo)}`} label={t('air_temp')} />
            ) : (
              <Stat value={fmt.temp(day.water_temp_mean_f)} label={t('water_temp')} />
            )
          }
          right={
            airShown && waterShown ? (
              <Stat value={fmt.temp(day.water_temp_mean_f)} label={t('water_temp')} />
            ) : null
          }
        />
      )}

      {(dayPpShown || dayThunderShown) && (
        <WeekRow
          left={<RowLabel>{t('chances')}</RowLabel>}
          time={
            dayPpShown ? (
              <Stat
                value={fmt.percent(dayPp)}
                label={conditionLabel(t, precipConditionCode(day))}
              />
            ) : (
              <Stat value={fmt.percent(dayThunderPct)} label={t('weather_thunder')} />
            )
          }
          right={
            dayPpShown && dayThunderShown ? (
              <Stat value={fmt.percent(dayThunderPct)} label={t('weather_thunder')} />
            ) : null
          }
        />
      )}

      {day.seas_max_ft != null && (
        <WeekRow
          left={<RowLabel>{t('max_seas')}</RowLabel>}
          time={
            // Same shape the hourly table's Seas cell uses — height,
            // direction, then period — so the day and the hours behind it
            // describe one swell the same way. The direction rides here
            // rather than in the row's third cell: it belongs to this
            // reading, not beside it as a second one.
            <Group gap={4} align="center" wrap="nowrap">
              <Text fz={DATA_FZ.value} fw={700} style={{ color: '#fff' }}>
                {fmt.height(null, day.seas_max_ft)}
              </Text>
              <DirectionLabel deg={peakSeas?.seas.direction_deg} />
              {peakSeas?.seas.period_s != null && (
                <>
                  <RowUnit>{t('seas_period_at')}</RowUnit>
                  <Text fz={DATA_FZ.value} fw={700} style={{ color: '#fff' }}>
                    {t('wave_period_short', { period: Math.round(peakSeas.seas.period_s) })}
                  </Text>
                </>
              )}
            </Group>
          }
        />
      )}

      {/* The day's worst visibility. No unit word beside it — fmt.visibility
        already carries the "nm", so a RowUnit here would be repeating it. */}
      {day.visibility_min_nm != null && (
        <WeekRow
          left={<RowLabel>{t('visibility')}</RowLabel>}
          time={
            <Text fz={DATA_FZ.value} fw={700} style={{ color: '#fff' }}>
              {fmt.visibility(day.visibility_min_nm)}
            </Text>
          }
        />
      )}

      {/* Presented exactly like TideChart is inside the Tides band: the same
        pt/pb/px inset, on the same TILE.dark panel at the same 10px radius.
        The darker panel is what reads as a chart rather than more rows, and
        using the tide curve's own treatment means the two charts in a day
        card are the same kind of object.

        The chart's own mt supplies the panel's top padding, so this adds
        none. Note the panel narrows the track slightly, and track width feeds
        pxToPct — which decides when two axis labels merge into a cluster. The
        shift is well under a knot's worth of threshold, but it is the reason
        to leave these numbers alone rather than nudge them by eye. */}
      <Box pt={12} pb={14} px={12}>
        <Box
          px={10}
          pb={12}
          style={{ background: TILE.dark, borderRadius: 10, overflow: 'hidden' }}
        >
          <WindSpeedChart
            wmin={wmin}
            wsust={wsust}
            gmax={gmax}
            windDeg={peakWind?.wind?.direction_deg}
          />
        </Box>
      </Box>

      {hours.length > 0 && (
        <UnstyledButton
          onClick={() => setOpen((o) => !o)}
          style={{ display: 'block', width: '100%', paddingBottom: 12 }}
        >
          <Group justify="center" gap={5}>
            <Text
              fz={DATA_FZ.label}
              fw={700}
              tt="uppercase"
              style={{ letterSpacing: CAPTION_LS, color: MUTED }}
            >
              {t('hourly_forecast')}
            </Text>
            <Text
              fz={DATA_FZ.value}
              lh={1}
              style={{
                color: MUTED,
                transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: 'transform .18s ease',
              }}
            >
              ▾
            </Text>
          </Group>
        </UnstyledButton>
      )}

      {open && <HourlyTable hours={hours} timezone={timezone} day={day} />}
    </>
  )
}

// The hourly forecast: a real table, one row per hour, one column per
// reading. Hourly answers a different question from the daily band above it —
// not "what kind of day is it" but "when does it turn", which is scanning
// DOWN a column rather than reading across a row. A reading you scan for has
// to sit at the same x-position in every row or the eye re-finds it two dozen
// times, so this is the one place in the card where a true grid beats prose.
//
// It scrolls inside its own container rather than wrapping or shrinking: the
// full set of columns cannot fit a 380px phone at the band's type size, and
// dropping to a smaller size to force the fit would trade the thing that
// makes a table work (legible aligned figures) for the thing it doesn't need
// (seeing every column at once). The body never scrolls sideways — only this.
// A capped height comes with that, so the heading row has something to stay
// pinned to; see the container below.
//
// Every cell centres, headings included. Right-aligning numerals is the usual
// advice, and it is right when a column holds bare figures you compare by
// their ragged edge — but only four of these do. Wind trails a direction
// arrow, seas trails an arrow and "@ 6s", so their digits never stacked
// anyway; right-aligning those lines up the arrows instead and leaves the
// numbers a digit-width apart. Centring at least makes every column
// consistent, and the rules below carry the boundary that alignment would
// otherwise have to. Figures stay tabular so digits keep a fixed width and
// don't shimmy between rows. Column headings are the one legitimate home left
// for the small dimmed Caption: they label a column rather than being
// readings.
//
// Columns are declared as data rather than written out as markup so that
// `has` does double duty — it decides both whether a whole column is worth
// its width across the day and whether a given hour has anything to put in
// it. A column no hour can fill is dropped entirely (a dry week should not
// pay for a Rain column, and a sheltered station has no seas at all), while
// inside a column that survives, an hour without a value gets a dash. That
// distinction matters: a dash says "not this hour", where a missing column
// says "not here, ever" — and neither should be a zero.
function HourlyTable({ hours, timezone, day }) {
  const { t } = useI18n()
  const fmt = useFormat()

  if (!hours.length) return null

  // The daily row's own visibility, as rendered — the reference the hourly
  // visibility column is judged against below.
  const dayVisibility =
    day?.visibility_min_nm != null ? fmt.visibility(day.visibility_min_nm) : null

  const pct = (v) => (v == null ? 0 : Math.round(v))
  const num = (children) => (
    <Text fz={DATA_FZ.value} fw={700} style={{ color: '#fff' }}>
      {children}
    </Text>
  )

  const columns = [
    {
      label: t('time'),
      has: () => true,
      cell: (h) => (
        <Text fz={DATA_FZ.value} style={{ color: '#fff' }}>
          {fmt.timeHour(h.time, timezone)}
        </Text>
      ),
    },
    {
      // No heading: the glyph is self-describing, and "Conditions" would be
      // the widest heading in the table for the narrowest column in it.
      label: '',
      has: () => true,
      cell: (h) => (
        <Text fz={GLYPH_FZ} lh={1}>
          {conditionGlyph(
            thunderSignificant(h.thunder) ? 'thunderstorms' : h.conditions,
            isNightHour(h.time, timezone),
            22,
          )}
        </Text>
      ),
    },
    {
      label: t('air_temp'),
      has: (h) => h.air_temp_f != null,
      cell: (h) => num(fmt.temp(h.air_temp_f)),
    },
    {
      // Above 0%, not the daily row's >15%: that bar exists because the daily
      // reading has to earn a place on a line the whole card sees, while here
      // the column is already paid for and a real 5% is worth knowing before
      // a passage. No precip_rate_inhr fallback — past the ~36h horizon the
      // probability goes null while conditions is still derived from the
      // rate, and an in/hr figure under a column headed Rain mixes units into
      // a column whose entire value is having exactly one. The glyph column
      // already says the hour is precipitating.
      // Named for what is actually falling that day — Snow, Sleet, Freezing
      // rain — via the same precipConditionCode the daily row above uses, so
      // the two can't disagree about a number they both report. A heading is
      // per-column, so a day that turns from rain to snow takes the worse of
      // the two here; the glyph column says which kind each individual hour
      // is, which is where that detail belongs anyway.
      label: conditionLabel(t, precipConditionCode(day)),
      has: (h) => pct(h.precip_probability_pct) > 0,
      cell: (h) => num(fmt.percent(h.precip_probability_pct)),
    },
    {
      label: t('weather_thunder'),
      has: (h) => pct(h.thunder?.probability_pct) > 0,
      cell: (h) => num(fmt.percent(h.thunder.probability_pct)),
    },
    {
      label: t('wind'),
      has: (h) => h.wind?.speed_kt != null,
      cell: (h) => (
        <Group gap={4} justify="center" wrap="nowrap" align="center">
          {num(fmt.wind(h.wind.speed_kt))}
          <DirectionLabel deg={h.wind.direction_deg} />
        </Group>
      ),
    },
    {
      // A gust only means something when it is actually above the sustained
      // wind. Compared as ROUNDED, the way the cell renders it: 13.4 and 12.6
      // are a different Number but both read "13 kt", and a gust that reads
      // the same as the wind beside it tells you nothing while looking like
      // it does. Same test the wind chart uses to decide whether its gust
      // label is worth drawing.
      //
      // Hours that fail it get a dash, and a day where no hour has a gust
      // worth the name drops the column outright — `has` governs both, so
      // the two answers can't disagree.
      label: t('gusts'),
      has: (h) =>
        h.gusts_kt != null &&
        (h.wind?.speed_kt == null || Math.round(h.gusts_kt) > Math.round(h.wind.speed_kt)),
      cell: (h) => <RowUnit>{fmt.wind(h.gusts_kt)}</RowUnit>,
    },
    {
      // Height, direction and period ride in one cell rather than three
      // columns: they are one reading about the same swell, and two of them
      // would otherwise hold columns narrower than their own headings.
      label: t('seas'),
      has: (h) => h.seas?.height_ft != null,
      cell: (h) => (
        <Group gap={4} justify="center" wrap="nowrap" align="center">
          {num(fmt.height(null, h.seas.height_ft))}
          <DirectionLabel deg={h.seas.direction_deg} />
          {h.seas.period_s != null && (
            <>
              <RowUnit>{t('seas_period_at')}</RowUnit>
              {num(t('wave_period_short', { period: Math.round(h.seas.period_s) }))}
            </>
          )}
        </Group>
      ),
    },
    {
      label: t('visibility'),
      has: (h) => h.visibility_nm != null,
      // Only worth a column when some hour actually reads differently from
      // the figure the daily row above already gives. Visibility spends most
      // of its life pegged at the ceiling, so on a clear day this is
      // twenty-four identical "10 nm" cells restating one number — the column
      // earns its width on the fog mornings and disappears the rest of the
      // time.
      //
      // Compared as RENDERED, not as raw floats: 10.02 and 9.97 are a
      // different Number but the same "10 nm", and a reader can only tell
      // cells apart by what the cell says. Comparing the floats would find a
      // difference essentially always and never hide anything.
      show: (hs) => {
        if (dayVisibility == null) return hs.some((h) => h.visibility_nm != null)
        return hs.some(
          (h) => h.visibility_nm != null && fmt.visibility(h.visibility_nm) !== dayVisibility,
        )
      },
      cell: (h) => <RowUnit>{fmt.visibility(h.visibility_nm)}</RowUnit>,
    },
    // `show` overrides the default test when a column needs more than "does
    // any hour have a value" to earn its width.
  ].filter((c) => (c.show ? c.show(hours) : hours.some(c.has)))

  // Scrolls in BOTH axes, and the vertical one is not optional. Setting
  // overflow-x: auto already makes this a scroll container on both axes — per
  // spec, `visible` computes to `auto` when the other axis isn't visible —
  // which makes it the containing block for the sticky header below. A header
  // stuck to a container that never scrolls vertically simply never moves, so
  // pinning the heading row means giving this box a height to scroll within.
  // overscrollBehavior stops the page from taking over the moment the table
  // hits its end.
  return (
    <Box
      style={{
        overflow: 'auto',
        maxHeight: '60vh',
        overscrollBehavior: 'contain',
      }}
    >
      <Box
        component="table"
        style={{
          // `separate`, not `collapse`: a sticky <th> is unreliable under
          // border-collapse — collapsed borders belong to the table rather
          // than the cell, so they detach and stay behind when the header
          // lifts. Nothing about the rendering changes here, because every
          // border is a top or a left and so has nothing to collapse against.
          borderCollapse: 'separate',
          borderSpacing: 0,
          width: '100%',
          whiteSpace: 'nowrap',
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        <thead>
          <tr>
            {columns.map((c, i) => (
              <Box
                component="th"
                key={i}
                px={10}
                pt={10}
                pb={7}
                style={{
                  textAlign: 'center',
                  fontWeight: 400,
                  borderLeft: i ? COL_RULE : undefined,
                  // Held at the top of the viewport while the day's hours
                  // scroll past, so a number two dozen rows down still has a
                  // heading. Needs its own opaque background (the rows would
                  // otherwise show through) and a bottom rule, since the row
                  // border that normally draws that line scrolls underneath.
                  position: 'sticky',
                  top: 0,
                  zIndex: 1,
                  background: TILE.mid,
                  borderBottom: BORDER,
                }}
              >
                <Caption>{c.label}</Caption>
              </Box>
            ))}
          </tr>
        </thead>
        <tbody>
          {hours.map((h, row) => (
            <Box component="tr" key={h.time}>
              {columns.map((c, i) => (
                <Box
                  component="td"
                  key={i}
                  px={10}
                  py={8}
                  style={{
                    textAlign: 'center',
                    // The first row skips its top border — the sticky header
                    // already draws one, and two adjacent 1px rules read as a
                    // heavy 2px line.
                    borderTop: row ? BORDER : undefined,
                    borderLeft: i ? COL_RULE : undefined,
                  }}
                >
                  {c.has(h) ? c.cell(h) : <Dash />}
                </Box>
              ))}
            </Box>
          ))}
        </tbody>
      </Box>
    </Box>
  )
}

// Vertical rule between columns, fainter than the horizontal row borders so
// the table still reads as rows first. It does more work than decoration
// here: a heading is often far wider than its values ("THUNDER" over "40%",
// "VISIBILITY" over "13"), so each column is sized by whichever is wider and
// the gaps between them come out uneven. Left unruled, that spacing actively
// misleads — two tight columns look like one field, a wide one looks like a
// break — and with everything centred there is no shared edge to infer a
// boundary from either. The rules ARE the column boundary.
const COL_RULE = '1px solid rgba(255,255,255,.09)'

// An hour with nothing to say in a column that other hours do fill. Dimmed
// rather than white: it is the absence of a reading, not a reading, and
// should not catch the eye being scanned down the column past it.
function Dash() {
  return (
    <Text fz={DATA_FZ.value} style={{ color: TILE_TEXT.faint }}>
      –
    </Text>
  )
}
